/**
 * PeopleAccessView (SETTINGS-ACCESS-01, Seth 2026-09-03: "users & roles and
 * sign-in security — confusing… counterintuitive and messy") — ONE page for
 * who can get into Pegasus and what they can do:
 *   People        — the roster (every active employee) with login status,
 *                   role, two-factor, last sign-in; click a person → the panel
 *                   (login actions, role, Also allow / Never extras).
 *   Roles         — the capability × role matrix (unchanged).
 *   Sign-in rules — two-factor policy, the invite switch + link domains,
 *                   the access log.
 * Your own two-factor lives in the user menu ("My sign-in").
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ShieldCheck, UserRound, Users } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions/PermissionProvider";
import { buildPeople, STATE_META, useAccessStatus, useLogins, useRoles, useRoster, type Person } from "./access";
import { PersonAccessPanel } from "./PersonAccessPanel";
import { PermissionMatrix } from "./RolesMatrix";
import { SignInRules } from "./SignInRules";

type Tab = "people" | "roles" | "signin";
type Filter = "all" | "logins" | "none" | "extras";

export function PeopleAccessView() {
  const { isAdmin, can } = usePermissions();
  const allowed = isAdmin || can("users.manage");
  const [params, setParams] = useSearchParams();
  const tab = ((params.get("tab") as Tab | null) ?? "people");
  const setTab = (t: Tab) => { const p = new URLSearchParams(params); p.set("tab", t); setParams(p, { replace: true }); };
  const roster = useRoster().data ?? [];
  const logins = useLogins().data ?? [];
  const roles = useRoles().data ?? [];
  const status = useAccessStatus(allowed).data ?? new Map();
  const people = useMemo(() => buildPeople(roster, logins, status, roles), [roster, logins, status, roles]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const selectedKey = params.get("person");
  const selected = people.find((p) => p.key === selectedKey || (p.empno && p.empno === selectedKey)) ?? null;
  const select = (p: Person | null) => { const n = new URLSearchParams(params); if (p) n.set("person", p.key); else n.delete("person"); setParams(n, { replace: true }); };

  if (!allowed) {
    return (
      <Card className="mx-auto mt-8 max-w-md p-8 text-center">
        <ShieldCheck className="mx-auto h-8 w-8 text-slate-300" />
        <p className="mt-3 text-sm text-slate-600">Your role can't manage people and access.</p>
      </Card>
    );
  }

  const t = q.trim().toLowerCase();
  const shown = people.filter((p) => {
    if (filter === "logins" && !p.login) return false;
    if (filter === "none" && p.login) return false;
    if (filter === "extras" && p.extras === 0) return false;
    if (!t) return true;
    return `${p.name} ${p.login?.email ?? ""} ${p.rosterEmail ?? ""} ${p.dept ?? ""} ${p.skill ?? ""} ${p.empno ?? ""}`.toLowerCase().includes(t);
  });
  const counts = { logins: people.filter((p) => p.login).length, none: people.filter((p) => !p.login).length, extras: people.filter((p) => p.extras > 0).length };
  const roleCounts = Object.fromEntries(roles.map((r) => [r.key, logins.filter((u) => u.role_key === r.key).length]));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-1 rounded-xl bg-slate-100/80 p-1">
        {([["people", "People", Users], ["roles", "Roles", ShieldCheck], ["signin", "Sign-in rules", UserRound]] as const).map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-semibold transition-colors", tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "roles" && <PermissionMatrix roles={roles} userCounts={roleCounts} />}
      {tab === "signin" && <SignInRules />}
      {tab === "people" && (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <div className="relative min-w-[200px] flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…"
                  className="h-9 w-full rounded-lg border border-hairline bg-white pl-8 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
              </div>
              {([["all", `All ${people.length}`], ["logins", `Has a login ${counts.logins}`], ["none", `No login ${counts.none}`], ["extras", `Extras ${counts.extras}`]] as const).map(([k, label]) => (
                <button key={k} type="button" onClick={() => setFilter(k)}
                  className={cn("rounded-full px-2.5 py-1 text-[11.5px] font-semibold transition-colors", filter === k ? "bg-brand-700 text-white" : "border border-hairline bg-white text-slate-600 hover:bg-slate-50")}>{label}</button>
              ))}
              <InfoTip label="Who's listed" align="end">
                Everyone on the ESC roster is listed, and a person without a login can be given one here. Inactive employees drop off
                once their login is gone.
              </InfoTip>
            </div>
            <Card className="overflow-hidden">
              <ul className="divide-y divide-slate-100">
                {shown.length === 0 && <li className="px-4 py-6 text-center text-xs text-slate-400">Nobody matches.</li>}
                {shown.map((p) => {
                  const st = STATE_META[p.state];
                  const active = selected?.key === p.key;
                  return (
                    <li key={p.key}>
                      <button type="button" onClick={() => select(active ? null : p)}
                        className={cn("flex w-full items-center gap-3 px-3 py-2 text-left transition-colors", active ? "bg-brand-50/60" : "hover:bg-slate-50")}>
                        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold", p.login ? "bg-brand-50 text-brand-700" : "bg-slate-100 text-slate-400")}>
                          {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className="truncate text-sm font-semibold text-slate-800">{p.name}</span>
                            <span className={cn("rounded-full px-1.5 py-px text-[10px] font-semibold", st.chip)}>{st.label}</span>
                            {p.status?.mfa && <span className="rounded-full bg-emerald-50 px-1.5 py-px text-[10px] font-semibold text-emerald-700" title="Two-factor on">2FA</span>}
                          </span>
                          <span className="block truncate text-[11px] text-slate-500">
                            {p.login ? `${p.role?.name ?? p.login.role_key}${p.extras ? ` · ${p.extras} extra${p.extras === 1 ? "" : "s"}` : ""}${p.status?.last_sign_in_at ? ` · ${new Date(p.status.last_sign_in_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}` : [p.dept, p.skill].filter(Boolean).join(" · ") || "—"}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </div>
          <div className="min-w-0">
            {selected ? (
              <PersonAccessPanel person={selected} roles={roles} onClose={() => select(null)} />
            ) : (
              <Card className="p-8 text-center">
                <UserRound className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                  Pick a person
                  <InfoTip label="What you can do here">
                    Create or manage their login, set their role, and add the few extra things they can do, all in one place.
                  </InfoTip>
                </p>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
